import { MethodType } from 'components/DropDown/MethodType';
import { fetchData, fetchDataReadWrite, getData, postData } from 'data/DataConnector';
import { isEqual } from 'lodash';
// import { isEqual } from 'lodash'
import React, { memo } from 'react';
import uniqid from 'uniqid';
import { shallowCompare } from 'util/helpers/helperFunctions';
import ValidationControl from '../../ValidationControl/ValidationControl';
import DropDownControl from '../DropdownControl';

const LOADING = 'Loading...';

export interface IProps {
  value: string;
  name?: string;
  isReadonly?: boolean;
  displayField: string;
  valueField: string;
  onChange?: (event: any) => void;
  methodType: MethodType;
  methodRoute?: string;
  parameters?: any;
  selectOption?: string;
  selectOptionValue?: string;
  isRequired?: boolean;
  isValid?: boolean;
  showAddModalLabel?: boolean;
  addModalLabelText?: string;
  onAddModalLabelClick?: () => any;
  onModalSave?: any;
  validationMessage?: string;
  isDisabled?: boolean;
  style?: any;
  hideValidationControl?: boolean;
  getDefaultValue?: (value: any, name?: string, data?: any) => void;
  getInitialSelectedText?: (selectedText: string) => void;
  methodName?: string;
  controllerName?: string;
  theme?: string;
  readWriteRequired?: boolean;
  dataTestId?: string;
  allowAdd?: boolean;
  onAddClick?: () => any;
  addTooltip?: string;
  allowEdit?: boolean;
  onEditClick?: () => any;
  editTooltip?: string;
  allowLiteralNull?: boolean;
}

interface IState {
  data: any[];
}

export const DropDownControlSqlObject: React.FC<IProps> = (props) => {
  const [data, setData] = React.useState<any[]>([
    { [props.valueField]: '', [props.displayField]: LOADING },
  ]);
  const uid = uniqid();

  React.useEffect(() => {
    fetchDataWrapper(
      props.methodRoute || '',
      props.parameters,
      props.methodType,
      props.controllerName,
      props.methodName,
    );
  }, []);

  React.useEffect(() => {
    setData([{ [props.valueField]: '', [props.displayField]: LOADING }]);
    fetchDataWrapper(
      props.methodRoute || '',
      props.parameters,
      props.methodType,
      props.controllerName,
      props.methodName,
    );
  }, [
    props.onModalSave,
    props.methodRoute,
    props.methodType,
    props.parameters,
    props.selectOption,
    props.methodName,
    props.controllerName,
  ]);

  const fetchDataWrapper = (
    methodRoute: string,
    parameters: any,
    methodType: MethodType,
    controllerName?: string,
    methodName?: string,
  ) => {
    if (controllerName && methodName) {
      if (props.readWriteRequired) {
        fetchDataReadWrite(controllerName, methodName, methodType, parameters).then(response => {
          processResponse(response.dataObject.data);
        });
      } else {
        fetchData(controllerName, methodName, methodType, parameters).then(response => {
          processResponse(response.dataObject.data);
        });
      }
    } else if (methodType === MethodType.Get) {
      getData(methodRoute, parameters, !props.readWriteRequired).then(response => {
        processResponse(JSON.parse(response).recordSets[0].data);
      });
    } else if (methodType === MethodType.Post) {
      postData(methodRoute, parameters).then(response => {
        processResponse(JSON.parse(response).recordSets[0].data);
      });
    }
  };

  const processResponse = (resultSet: any) => {
    const initialData = getInitialList(
      props.valueField,
      props.displayField,
      props.selectOption,
      props.selectOptionValue,
    );
    setData([...initialData, ...resultSet]);
  };

  const getInitialList = (
    valueField: string,
    displayField: string,
    selectOption: string = '',
    selectOptionValue: string = '',
  ) => {
    let data: any[] = [];
    if (selectOption.indexOf(';') !== -1) {
      if (selectOptionValue.indexOf(';') !== -1) {
        const options: string[] = selectOption.split(';');
        const optionValues: string[] = selectOptionValue.split(';');
        if (options.length === optionValues.length) {
          options.forEach((element, index) => {
            data.push({
              [valueField]: optionValues[index],
              [displayField]: element,
            });
          });
        }
      }
    } else {
      if (selectOption) {
        data.push({
          [valueField]: selectOptionValue,
          [displayField]: selectOption,
        });
      }
    }
    return data;
  };

  return (
    <table>
      <tbody>
        <tr>
          {!props.hideValidationControl ? (
            <td>
              <ValidationControl
                {...props}
                isValid={props.isValid !== undefined ? props.isValid : true}
                message={props.validationMessage ? props.validationMessage : 'An input is required'}
                uid={uid}
              />
            </td>
          ) : (
            <></>
          )}
          <td>
            <DropDownControl
              {...props}
              isDisabled={
                data.length === 1 && data[0][props.displayField] === LOADING ? true : props.isDisabled
              }
              data={data}
              getDefaultValue={
                data.length === 1 && (props.selectOption || data[0][props.displayField] === LOADING)
                  ? undefined
                  : props.getDefaultValue
              }
              getInitialSelectedText={
                data.length === 1 && (props.selectOption || data[0][props.displayField] === LOADING)
                  ? undefined
                  : props.getInitialSelectedText
              }
            />
          </td>
        </tr>
      </tbody>
    </table>
  );
};

function arePropsEqual(prevProps: IProps, nextProps: IProps) {
  const oldProps = {
    ...prevProps,
  };
  const newProps = {
    ...nextProps,
  };
  if ('onChange' in oldProps) {
    delete oldProps.onChange;
  }
  if ('onChange' in newProps) {
    delete newProps.onChange;
  }
  if (oldProps.methodType === newProps.methodType) {
    if (oldProps.methodType === MethodType.Post) {
      if (!shallowCompare(oldProps.parameters, newProps.parameters)) {
        return false;
      } else {
        delete oldProps.parameters;
        delete newProps.parameters;
      }
    }
  }

  if ('onAddModalLabelClick' in oldProps) {
    delete oldProps.onAddModalLabelClick;
  }
  if ('onAddModalLabelClick' in newProps) {
    delete newProps.onAddModalLabelClick;
  }

  if ('onModalSave' in oldProps) {
    delete oldProps.onModalSave;
  }
  if ('onModalSave' in newProps) {
    delete newProps.onModalSave;
  }

  if ('onAddClick' in oldProps) {
    delete oldProps.onAddClick;
  }
  if ('onAddClick' in newProps) {
    delete newProps.onAddClick;
  }

  if ('onEditClick' in oldProps) {
    delete oldProps.onEditClick;
  }
  if ('onEditClick' in newProps) {
    delete newProps.onEditClick;
  }

  if ('style' in oldProps) {
    delete oldProps.style;
  }
  if ('style' in newProps) {
    delete newProps.style;
  }

  if ('getDefaultValue' in oldProps) {
    delete oldProps.getDefaultValue;
  }
  if ('getDefaultValue' in newProps) {
    delete newProps.getDefaultValue;
  }
  return shallowCompare(oldProps, newProps);
}

export default memo(DropDownControlSqlObject, arePropsEqual);
